import { SubmissionStatus } from "generated-graphql/graphql";
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
  SetStateAction,
  Dispatch,
} from "react";

import { Row } from "./types";

interface SelectionContextType {
  // Row selection
  selectedRows: Row[];
  selectedIds: string[];
  setSelectedRows: (rows: Row[]) => void;
  setSelectedIds: (ids: string[]) => void;
  clearSelections: () => void;

  // Chart selection - supports functional updates to avoid callback dependencies on selection state
  selectedChartStatus: SubmissionStatus | null;
  selectedChartState: string | null;
  selectedChartGeneratorStatus: string | null;
  setSelectedChartStatus: Dispatch<SetStateAction<SubmissionStatus | null>>;
  setSelectedChartState: Dispatch<SetStateAction<string | null>>;
  setSelectedChartGeneratorStatus: Dispatch<SetStateAction<string | null>>;
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

export function SelectionProvider({ children }: { children: ReactNode }) {
  // Row selection state
  const [selectedRows, setSelectedRowsInternal] = useState<Row[]>([]);
  const [selectedIds, setSelectedIdsInternal] = useState<string[]>([]);

  // Chart selection state
  const [selectedChartStatus, setSelectedChartStatusInternal] = useState<SubmissionStatus | null>(
    null
  );
  const [selectedChartState, setSelectedChartStateInternal] = useState<string | null>(null);
  const [selectedChartGeneratorStatus, setSelectedChartGeneratorStatusInternal] = useState<
    string | null
  >(null);

  // Memoized setters to prevent unnecessary re-renders
  const setSelectedRows = useCallback((rows: Row[]) => {
    setSelectedRowsInternal(rows);
  }, []);

  const setSelectedIds = useCallback((ids: string[]) => {
    setSelectedIdsInternal(ids);
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedRowsInternal([]);
    setSelectedIdsInternal([]);
  }, []);

  // Chart selection setters support functional updates to avoid callback dependencies on selection state
  // This allows handlers like handleChartStatusClick to use setSelectedChartStatus((prev) => ...)
  // without needing selectedChartStatus in their dependency array
  const setSelectedChartStatus: Dispatch<SetStateAction<SubmissionStatus | null>> = useCallback(
    (action) => {
      setSelectedChartStatusInternal(action);
    },
    []
  );

  const setSelectedChartState: Dispatch<SetStateAction<string | null>> = useCallback((action) => {
    setSelectedChartStateInternal(action);
  }, []);

  const setSelectedChartGeneratorStatus: Dispatch<SetStateAction<string | null>> = useCallback(
    (action) => {
      setSelectedChartGeneratorStatusInternal(action);
    },
    []
  );

  // Memoize the context value to prevent unnecessary re-renders of consumers
  // when unrelated state changes
  const value = useMemo(
    () => ({
      selectedRows,
      selectedIds,
      setSelectedRows,
      setSelectedIds,
      clearSelections,
      selectedChartStatus,
      selectedChartState,
      selectedChartGeneratorStatus,
      setSelectedChartStatus,
      setSelectedChartState,
      setSelectedChartGeneratorStatus,
    }),
    [
      selectedRows,
      selectedIds,
      setSelectedRows,
      setSelectedIds,
      clearSelections,
      selectedChartStatus,
      selectedChartState,
      selectedChartGeneratorStatus,
      setSelectedChartStatus,
      setSelectedChartState,
      setSelectedChartGeneratorStatus,
    ]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelectionContext() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error("useSelectionContext must be used within a SelectionProvider");
  }
  return context;
}

// Granular hooks to minimize re-renders - consumers only subscribe to what they need
export function useRowSelection() {
  const { selectedRows, selectedIds, setSelectedRows, setSelectedIds, clearSelections } =
    useSelectionContext();
  return { selectedRows, selectedIds, setSelectedRows, setSelectedIds, clearSelections };
}

export function useChartSelection() {
  const {
    selectedChartStatus,
    selectedChartState,
    selectedChartGeneratorStatus,
    setSelectedChartStatus,
    setSelectedChartState,
    setSelectedChartGeneratorStatus,
  } = useSelectionContext();
  return {
    selectedChartStatus,
    selectedChartState,
    selectedChartGeneratorStatus,
    setSelectedChartStatus,
    setSelectedChartState,
    setSelectedChartGeneratorStatus,
  };
}
